import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { FlexLayout } from '@calendly/ui/components/flex-layout';
import { IconButton } from '@calendly/ui/components/icon-button';
import { CalendarIcon, CloseIcon, TimeClockCircleIcon } from '@calendly/ui/components/icons';
import { Modal } from '@calendly/ui/components/modal';
import { Sheet } from '@calendly/ui/components/sheet';
import { TitleText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { textColorL2 } from '@calendly/ui/theme';
import classNames from 'classnames';
import { useEffect, useRef, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closePollsBookingModal, setAdditionalInvitees, setInvitees, submitScheduling } from 'actions/polls/scheduling';
import { pollBookingSelector } from 'selectors/scheduled_events';
import formatPollTime from '../poll_time';
import { AddInvitee } from './add_invitee';
import { InviteesList } from './invitees_list';
import Location from './location';
import { WarningBanner } from './warning_banner';
const containerIsPhoneCss = "ccpe6o7";
const containerCss = "cgnbjhm";
const Container = forwardRef((props, ref) => {
  const isPhone = useDeviceType('isPhone');
  return <Sheet ref={ref} {...props} className={classNames(containerCss, {
    [containerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const scrollWrapperIsPhoneCss = "s1sge81a";
const scrollWrapperCss = "s1ju9q04";
const ScrollWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(scrollWrapperCss, {
    [scrollWrapperIsPhoneCss]: isPhone
  }, props.className)} />;
});
const headerCss = "h12d7ka2";
const Header = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerCss, props.className)} />;
});
const footerCss = "f1abt58s";
const Footer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(footerCss, props.className)} />;
});
const detailsIsPhoneCss = "d1xt3j9u";
const detailsCss = "d1f5gxii";
const Details = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(detailsCss, {
    [detailsIsPhoneCss]: isPhone
  }, props.className)} />;
});
const inviteesCountCss = "i1al8jos";
const InviteesCount = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'p';
  return <Component ref={ref} {...restProps} className={classNames(inviteesCountCss, props.className)} />;
});
const cancelButtonCss = "csnei58";
const CancelButton = forwardRef((props, ref) => {
  const decoration = useDeviceType('isPhone') ? 'secondary-outline' : 'ghost';
  return <Button ref={ref} {...props} decoration={decoration} className={classNames(cancelButtonCss, props.className)} />;
});
const BookingModal = () => {
  const {
    name,
    startTime,
    endTime,
    invitees,
    additionalInvitees,
    eventTypeId,
    hasExternalEventConflict,
    internalConflictEvents,
    locationConfiguration,
    modalVisible,
    isLoading
  } = useSelector(pollBookingSelector);
  const dispatch = useDispatch();
  const confirmationButtonRef = useRef();
  const inviteeIds = invitees.map(({
    id
  }) => id);
  const onClose = () => dispatch(closePollsBookingModal());
  const countOfAllInvitees = invitees.length + additionalInvitees.length;
  const pollDateTime = formatPollTime(startTime, endTime, 'date_extra');
  const removeInvitee = invitee => {
    if (invitee.id) {
      const newInvitees = invitees.filter(({
        id
      }) => invitee.id !== id);
      dispatch(setInvitees(newInvitees));
    } else {
      const newInvitees = additionalInvitees.filter(({
        email
      }) => invitee.email !== email);
      dispatch(setAdditionalInvitees(newInvitees));
    }
  };
  const scheduleEvent = async () => {
    await dispatch(submitScheduling({
      inviteeIds,
      startTime,
      eventTypeId,
      locationConfiguration,
      additionalInvitees
    }));
  };
  useEffect(() => {
    if (modalVisible) {
      confirmationButtonRef.current.focus();
    }
  }, [modalVisible]);
  return <Modal isOpen={modalVisible} onClose={onClose}>
      <Container data-component="booking-modal" data-testid="booking-modal">
        <ScrollWrapper>
          <Header>
            <TitleText level={2} as="h1" size="medium">
              {I18n.t('publisher.contacts.buttons.book_meeting')}
            </TitleText>
            <IconButton title={I18n.t('booking.button.close')} onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Header>
          <FlexLayout direction="column" gap={8}>
            <TitleText level={2} size="large">
              {name}
            </TitleText>
            <Details>
              <div>
                <CalendarIcon size="large" spacingRight={8} color={textColorL2} />
                {pollDateTime[0]}
              </div>
              <div>
                <TimeClockCircleIcon size="large" spacingRight={8} color={textColorL2} />
                {pollDateTime[1]}
              </div>
              <Location locationConfiguration={locationConfiguration} />
            </Details>
          </FlexLayout>
          <WarningBanner hasExternalEventConflict={hasExternalEventConflict} internalConflictEvents={internalConflictEvents} />
          <div>
            <InviteesCount>
              {I18n.t('publisher.scheduled_events.pending.booking_modal.invitees_count', {
              count: countOfAllInvitees
            })}
            </InviteesCount>
            <p>
              {I18n.t('publisher.scheduled_events.pending.booking_modal.invitees_notice')}
            </p>
            <AddInvitee />
            <InviteesList invitees={invitees} additionalInvitees={additionalInvitees} onClickRemove={removeInvitee} />
          </div>
        </ScrollWrapper>
        <Footer>
          <CancelButton onClick={onClose}>
            {I18n.t('publisher.action.cancel')}
          </CancelButton>
          <Button onClick={scheduleEvent} loading={isLoading} ref={confirmationButtonRef}>
            {I18n.t('publisher.scheduled_events.pending.booking_modal.book_meeting')}
          </Button>
        </Footer>
      </Container>
    </Modal>;
};
export default BookingModal;

require("./index.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./index.js");